import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import SectionHeading from "../NewHome/SectionHeading";
import { useBodyScrollLock } from "../../../hooks/useBodyScrollLock";
import axiosInstance from "../../../utils/axios";
import { FaPlay } from "react-icons/fa";

export const GalleryGrid = ({ galleryItems, onOpen }) => {
  const [selected, setSelected] = useState(null);
  const [selectedTitle, setSelectedTitle] = useState("");
  useBodyScrollLock(!!selected);

  const handleOpen = (item) => {
    setSelected(item);
    setSelectedTitle(item?.title || "");
    if (typeof onOpen === "function") onOpen(item);
  };

  const closeModal = useCallback(() => {
    setSelected(null);
    setSelectedTitle("");
  }, []);

  useEffect(() => {
    if (!selected) return;
    const handleEsc = (e) => {
      if (e.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [selected, closeModal]);

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6 py-6 md:py-10">
        {galleryItems.map((item, index) => (
          <motion.div
            key={item._id ?? index}
            className="flex flex-col space-y-3"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: index * 0.06 }}
            viewport={{ once: true }}
          >
            {/* Card */}
            <div
              className="overflow-hidden rounded-lg shadow-md cursor-pointer group bg-white hover:shadow-xl transition-all duration-300"
              onClick={() => handleOpen(item)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && handleOpen(item)}
            >
              <div className="relative overflow-hidden">
                {item.category === "video" ? (
                  <>
                    {/* show thumbnail if we can (fileUrl may be a video) */}
                    <div className="w-full h-56 md:h-64 bg-black/5">
                      <video
                        src={item.fileUrl}
                        className="w-full h-full object-cover"
                        preload="metadata"
                        muted
                        playsInline
                      />
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-white/90 backdrop-blur-sm text-gray-700 p-3 rounded-full shadow-lg transform scale-90 group-hover:scale-100 transition-transform duration-200">
                        <FaPlay className="w-5 h-5" />
                      </div>
                    </div>
                  </>
                ) : (
                  <img
                    src={item.fileUrl}
                    alt={item.title || "gallery image"}
                    className="w-full h-56 md:h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                )}

                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300" />
              </div>
            </div>

            {/* Title - only if exists */}
            {item.title && (
              <motion.h3
                className="text-center text-[#333] font-semibold text-sm sm:text-base px-2 leading-tight"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.45, delay: index * 0.04 + 0.12 }}
                viewport={{ once: true }}
              >
                {item.title}
              </motion.h3>
            )}
          </motion.div>
        ))}
      </div>

      {/* Lightbox / Modal */}
      <AnimatePresence>
        {selected && (
          <motion.div
            className="fixed max-h-screen inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="gallery-lightbox-title"
          >
            <motion.div
              className="relative max-w-6xl w-full"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 250, damping: 24 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeModal}
                className="absolute -top-12 right-0 text-white bg-black/50 hover:bg-black/70 p-3 rounded-full transition-colors duration-200 z-10"
                aria-label="Close image preview"
              >
                <X className=" size-4 sm:size-12" />
              </button>

              <div className="absolute hidden sm:block -top-10 left-0 text-white/70 text-sm bg-black/50 px-3 py-2 rounded-lg">
                Press ESC to close
              </div>

              {selected.category === "video" ? (
                <video
                  src={selected.fileUrl}
                  controls
                  autoPlay
                  className="w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
                />
              ) : (
                <img
                  src={selected.fileUrl}
                  alt={selectedTitle || "gallery"}
                  className="w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
                  style={{
                    maxHeight: "calc(100vh - 120px)",
                    maxWidth: "calc(100vw - 32px)",
                  }}
                />
              )}

              {selectedTitle && (
                <div className="mt-2 text-center">
                  <h3
                    id="gallery-lightbox-title"
                    className="text-white text-lg md:text-xl font-semibold"
                  >
                    {selectedTitle}
                  </h3>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const GallerySkeleton = ({ count = 8 }) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6 py-6 md:py-10">
      {Array.from({ length: count }).map((_, idx) => (
        <div key={idx} className="animate-pulse flex flex-col space-y-3">
          <div className="w-full h-56 md:h-64 bg-gray-300 rounded-lg" />
          <div className="w-3/4 h-4 bg-gray-300 rounded mx-auto" />
        </div>
      ))}
    </div>
  );
};

const GalleryError = ({ message, onRetry }) => {
  return (
    <div className="flex flex-col items-center justify-center py-16 space-y-4 text-center">
      <div className="text-red-500 text-6xl">⚠️</div>
      <h2 className="text-xl font-semibold text-gray-700">
        Oops! Something went wrong
      </h2>
      <p className="text-gray-500 max-w-xl">{message}</p>
      <button
        onClick={onRetry}
        className="px-6 py-2 bg-red-500 text-white rounded-lg shadow hover:bg-red-600 transition"
      >
        Retry
      </button>
    </div>
  );
};

const OurGallery = () => {
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("image");

  const fetchGallery = useCallback(async (category = "image") => {
    try {
      setLoading(true);
      setError("");
      const url = category ? `/gallery?category=${category}` : "/gallery";
      const resp = await axiosInstance.get(url);
      // support both { data: ... } and { success: true, data: [...] }
      const data = resp?.data?.data ?? resp?.data ?? [];
      setGalleryItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching gallery,", err);
      setError(
        err?.response?.data?.message ?? err?.message ?? "Couldn't load gallery"
      );
      setGalleryItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // fetch when activeTab changes
  useEffect(() => {
    fetchGallery(activeTab);
  }, [activeTab, fetchGallery]);

  // initial fetch (redundant with above but ensures load if default changes)
  useEffect(() => {
    fetchGallery(activeTab);
  }, [fetchGallery, activeTab]);

  const tabs = [
    { key: "image", label: "Images" },
    { key: "video", label: "Videos" },
    { key: "media", label: "Media Files" },
  ];

  return (
    <div className="px-4 py-6 md:px-10 md:py-10 bg-[#ECF8FB] overflow-hidden">
      <SectionHeading
        subtitle={"Our Gallery"}
        title={`"Memories We've Created Along the Way"`}
      />

      <div className="flex justify-center gap-6 mt-6">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-6 py-2 text-sm md:text-base font-semibold rounded-full transition-all duration-300 ${
              activeTab === tab.key
                ? "bg-[#02323F] text-white shadow-lg"
                : "bg-white text-gray-600 hover:bg-gray-100"
            }`}
            aria-pressed={activeTab === tab.key}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading && <GallerySkeleton />}

      {!loading && error && (
        <GalleryError message={error} onRetry={() => fetchGallery(activeTab)} />
      )}

      {!loading && !error && (
        <GalleryGrid
          galleryItems={galleryItems}
          onOpen={() => {
            /* optional hook if parent needs it */
          }}
        />
      )}
    </div>
  );
};

export default OurGallery;

// import React, { useEffect, useState } from "react";
// import { motion, AnimatePresence } from "framer-motion";
// import { X } from "lucide-react";
// import SectionHeading from "../NewHome/SectionHeading";
// import { useBodyScrollLock } from "../../../hooks/useBodyScrollLock";
// import axiosInstance from "../../../utils/axios";

// export const GalleryGrid = ({ galleryItems }) => {
//   const [selectedImg, setSelectedImg] = useState(null);
//   const [selectedTitle, setSelectedTitle] = useState("");
//   useBodyScrollLock(selectedImg);
//   const handleImageClick = (item) => {
//     setSelectedImg(item.image);
//     setSelectedTitle(item.title);
//   };

//   const closeModal = () => {
//     setSelectedImg(null);
//     setSelectedTitle("");
//   };

//   // Handle keyboard navigation
//   React.useEffect(() => {
//     const handleEscapeKey = (event) => {
//       if (event.key === "Escape") {
//         closeModal();
//       }
//     };

//     if (selectedImg) {
//       document.addEventListener("keydown", handleEscapeKey);
//       return () => document.removeEventListener("keydown", handleEscapeKey);
//     }
//   }, [selectedImg]);
//   return (
//     <div>
//       <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 py-6 md:py-10">
//         {galleryItems.map((item, index) => (
//           <motion.div
//             key={index}
//             className="flex flex-col space-y-3"
//             initial={{ opacity: 0, y: 30 }}
//             whileInView={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.6, delay: index * 0.08 }}
//             viewport={{ once: true }}
//           >
//             {/* Image Card */}
//             <div
//               className="overflow-hidden rounded-lg shadow-md cursor-pointer group bg-white hover:shadow-xl transition-all duration-300"
//               onClick={() => handleImageClick(item)}
//             >
//               <div className="relative overflow-hidden">
//                 <img
//                   src={item.image}
//                   alt={item.title}
//                   className="w-full h-56 md:h-64 object-cover group-hover:scale-105 transition-transform duration-500"
//                   loading="lazy"
//                 />
//                 {/* Subtle overlay on hover */}
//                 <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300" />

//                 {/* Hover icon */}
//                 <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
//                   <div className="bg-white/90 backdrop-blur-sm text-gray-700 p-3 rounded-full shadow-lg transform scale-75 group-hover:scale-100 transition-transform duration-300">
//                     <svg
//                       className="w-6 h-6"
//                       fill="none"
//                       stroke="currentColor"
//                       viewBox="0 0 24 24"
//                     >
//                       <path
//                         strokeLinecap="round"
//                         strokeLinejoin="round"
//                         strokeWidth={2}
//                         d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
//                       />
//                     </svg>
//                   </div>
//                 </div>
//               </div>
//             </div>
//             {/* Title - Outside the card */}
//             {item.title && (
//               <motion.h3
//                 className="text-center text-[#333] font-semibold text-sm sm:text-base px-2 leading-tight"
//                 initial={{ opacity: 0 }}
//                 whileInView={{ opacity: 1 }}
//                 transition={{ duration: 0.6, delay: index * 0.08 + 0.2 }}
//                 viewport={{ once: true }}
//               >
//                 {item.title}
//               </motion.h3>
//             )}
//           </motion.div>
//         ))}
//       </div>
//       {/* Enhanced Fullscreen Popup (Lightbox) */}
//       <AnimatePresence>
//         {selectedImg && (
//           <motion.div
//             className="fixed max-h-screen inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4"
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             exit={{ opacity: 0 }}
//             onClick={closeModal}
//             role="dialog"
//             aria-modal="true"
//             aria-labelledby="image-modal-title"
//           >
//             <motion.div
//               className="relative max-w-6xl w-full"
//               initial={{ scale: 0.8, opacity: 0 }}
//               animate={{ scale: 1, opacity: 1 }}
//               exit={{ scale: 0.8, opacity: 0 }}
//               transition={{ type: "spring", stiffness: 200, damping: 20 }}
//               onClick={(e) => e.stopPropagation()}
//             >
//               {/* Close button */}
//               <button
//                 onClick={closeModal}
//                 className="absolute -top-12 right-0 text-white bg-black/50 hover:bg-black/70 p-3 rounded-full transition-colors duration-200 z-10"
//                 aria-label="Close image preview"
//               >
//                 <X size={24} />
//               </button>

//               {/* ESC hint */}
//               <div className="absolute -top-10 left-0 text-white/70 text-sm bg-black/50 px-3 py-2 rounded-lg">
//                 Press ESC to close
//               </div>

//               {/* Image */}
//               <img
//                 src={selectedImg}
//                 alt={selectedTitle}
//                 className="w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
//                 style={{
//                   maxHeight: "calc(100vh - 120px)",
//                   maxWidth: "calc(100vw - 32px)",
//                 }}
//               />

//               {/* Image title */}
//               {selectedTitle && (
//                 <div className="mt-2 text-center">
//                   <h3
//                     id="image-modal-title"
//                     className="text-white text-lg md:text-xl font-semibold"
//                   >
//                     {selectedTitle}
//                   </h3>
//                 </div>
//               )}
//             </motion.div>
//           </motion.div>
//         )}
//       </AnimatePresence>
//     </div>
//   );
// };

// const GallerySkeleton = () => {
//   return (
//     <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 py-6 md:py-10">
//       {Array.from({ length: 8 }).map((_, idx) => (
//         <div key={idx} className="animate-pulse flex flex-col space-y-3">
//           <div className="w-full h-56 md:h-64 bg-gray-300 rounded-lg" />
//           <div className="w-3/4 h-4 bg-gray-300 rounded mx-auto" />
//         </div>
//       ))}
//     </div>
//   );
// };

// const GalleryError = ({ message, onRetry }) => {
//   return (
//     <div className="flex flex-col items-center justify-center py-16 space-y-4 text-center">
//       <div className="text-red-500 text-6xl">⚠️</div>
//       <h2 className="text-xl font-semibold text-gray-700">
//         Oops! Something went wrong
//       </h2>
//       <p className="text-gray-500">{message}</p>
//       <button
//         onClick={onRetry}
//         className="px-6 py-2 bg-red-500 text-white rounded-lg shadow hover:bg-red-600 transition"
//       >
//         Retry
//       </button>
//     </div>
//   );
// };

// const OurGallery = () => {
//   const [galleryItems, setGalleryItems] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState("");
//   const [activeTab, setActiveTab] = useState("image");

//   const fetchGallery = async (category) => {
//     try {
//       setLoading(true);
//       setError("");

//       const resp = await axiosInstance.get(
//         `/gallery${category ? `?category=${category}` : ""}`
//       );
//       console.log("Data===>", resp);
//       setGalleryItems(resp?.data || []);
//     } catch (error) {
//       console.log("Error fetching gallery,", error);
//       setError(error?.message || "Couldn't load image");
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Fetch when activeTab changes
//   useEffect(() => {
//     fetchGallery(activeTab);
//   }, [activeTab]);

//   useEffect(() => {
//     fetchGallery();
//   }, []);

//   // Tabs
//   const tabs = [
//     { key: "image", label: "Images" },
//     { key: "video", label: "Videos" },
//     { key: "media", label: "Media Files" },
//   ];

//   return (
//     <div className="px-4 py-6 md:px-10 md:py-10 bg-[#ECF8FB] overflow-hidden">
//       <SectionHeading
//         subtitle={"Our Gallery"}
//         title={`"Memories We've Created Along the Way"`}
//       />

//       <div className="flex justify-center gap-6 mt-6">
//         {tabs.map((tab) => (
//           <button
//             key={tab.key}
//             onClick={() => setActiveTab(tab.key)}
//             className={`px-6 py-2 text-sm md:text-base font-semibold rounded-full transition-all duration-300
//       ${
//         activeTab === tab.key
//           ? "bg-[#02323F] text-white shadow-lg"
//           : "bg-white text-gray-600 hover:bg-gray-100"
//       }`}
//           >
//             {tab.label}
//           </button>
//         ))}
//       </div>

//       {loading && <GallerySkeleton />}
//       {error && <GalleryError message={error} onRetry={fetchGallery} />}
//       {!loading && !error && <GalleryGrid galleryItems={galleryItems} />}
//     </div>
//   );
// };

// export default OurGallery;
